import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { WorkItem } from '@/types'
import { Flag, X } from 'lucide-react'
import { formatWorkItemNumber } from '@/lib/workItemFormat'

interface BulkUpdateStatusDialogProps {
  workItems: WorkItem[]
  targetStatus: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isPending: boolean
}

export function BulkUpdateStatusDialog({
  workItems,
  targetStatus,
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: BulkUpdateStatusDialogProps) {
  if (targetStatus === null) return null

  const count = workItems.length
  const noun = count === 1 ? 'work item' : 'work items'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
            Move {count} {noun} to "{targetStatus}"?
          </DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none" aria-label="Close">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          The status <span className="font-bold">{targetStatus}</span> will be applied to every selected {noun}, whatever
          each one reads today.
        </DialogDescription>
        <div className="overflow-hidden">
          <p className="text-sm font-medium text-foreground mb-2">Selected {noun}:</p>
          <div className="max-h-[240px] overflow-y-auto flex flex-col gap-1">
            {workItems.map((workItem) => (
              <div
                key={workItem.id}
                className="flex items-center gap-3 rounded-lg border border-border px-3 py-2"
                data-testid="bulk-status-work-item-row"
              >
                <span className="w-12 shrink-0 text-sm font-medium text-muted-foreground">
                  {formatWorkItemNumber(workItem.number)}
                </span>
                <span className="flex-1 min-w-0 text-sm font-bold text-foreground truncate">
                  "{workItem.title}"
                </span>
                <span className="shrink-0"><WorkItemStatusBadge status={workItem.status} /></span>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isPending}
            data-testid="bulk-status-confirm-button"
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
          >
            <Flag className="h-6 w-6" />
            {isPending ? 'Updating...' : `Move to ${targetStatus}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
